import { createContext, useContext, useMemo, useState } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/800.css'
import { designSpecs } from '../theme/designSpecs'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light')

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: designSpecs.colors.primary.main,
            light: designSpecs.colors.primary.light,
            dark: designSpecs.colors.primary.hover,
          },
          secondary: {
            main: designSpecs.colors.secondary.main,
            light: designSpecs.colors.secondary.light,
            dark: designSpecs.colors.secondary.hover,
          },
          success: {
            main: designSpecs.colors.success.main,
            light: designSpecs.colors.success.light,
            dark: designSpecs.colors.success.dark,
          },
          warning: {
            main: designSpecs.colors.warning.main,
            light: designSpecs.colors.warning.light,
          },
          grey: designSpecs.colors.gray,
          background: {
            default: mode === 'light' ? designSpecs.colors.gray[50] : designSpecs.colors.gray[900],
            paper: mode === 'light' ? designSpecs.colors.white : designSpecs.colors.gray[800],
          },
        },
        typography: {
          fontFamily: designSpecs.typography.fontFamily.primary,
          fontWeightRegular: designSpecs.typography.fontWeights.regular,
          fontWeightMedium: designSpecs.typography.fontWeights.medium,
          fontWeightBold: designSpecs.typography.fontWeights.bold,
          h1: {
            fontWeight: designSpecs.typography.fontWeights.extrabold,
            fontSize: designSpecs.typography.sizes['5xl'],
            lineHeight: 1.2,
            '@media (min-width:1024px)': {
              fontSize: designSpecs.typography.sizes['7xl'],
            },
          },
          h2: {
            fontWeight: designSpecs.typography.fontWeights.extrabold,
            fontSize: designSpecs.typography.sizes['4xl'],
            lineHeight: 1.3,
            '@media (min-width:1024px)': {
              fontSize: designSpecs.typography.sizes['5xl'],
            },
          },
          h3: {
            fontWeight: designSpecs.typography.fontWeights.bold,
            fontSize: designSpecs.typography.sizes['2xl'],
            lineHeight: 1.4,
          },
          h4: {
            fontWeight: designSpecs.typography.fontWeights.bold,
            fontSize: designSpecs.typography.sizes.xl,
            lineHeight: 1.4,
          },
          h5: {
            fontWeight: designSpecs.typography.fontWeights.bold,
            fontSize: designSpecs.typography.sizes.lg,
            lineHeight: 1.5,
          },
          body1: {
            fontSize: designSpecs.typography.sizes.base,
            lineHeight: 1.6,
          },
          body2: {
            fontSize: designSpecs.typography.sizes.sm,
            lineHeight: 1.5,
          },
        },
        shape: {
          borderRadius: 12,
        },
        shadows: [
          'none',
          designSpecs.shadows.sm,
          designSpecs.shadows.md,
          designSpecs.shadows.lg,
          designSpecs.shadows.xl,
          designSpecs.shadows['2xl'],
          designSpecs.shadows.accent[30],
          designSpecs.shadows.accent[40],
          designSpecs.shadows.accent[60],
          // Fill rest with default shadows
          ...Array(16).fill(designSpecs.shadows.lg),
        ],
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: designSpecs.typography.fontWeights.semibold,
                borderRadius: designSpecs.borderRadius.xl,
                padding: '0.625rem 1.5rem',
                fontSize: designSpecs.typography.sizes.base,
                transition: designSpecs.transitions.default,
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              },
              sizeLarge: {
                padding: '1rem 2rem',
                fontSize: designSpecs.typography.sizes.lg,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: designSpecs.borderRadius.xl,
                boxShadow: designSpecs.shadows.lg,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: designSpecs.borderRadius.xl,
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: designSpecs.shadows.sm,
              },
            },
          },
        },
      }),
    [mode]
  )

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
